import { defineStore, logoutCallMap, PersistNames } from '@/utils/store'
import { useMemo } from 'react'
import { API_CONST, QueryClientInstance, setHeaders } from '@apis'
import { deleteSessionItem } from '@/website/useSessionStore'
import { deleteStorageItem } from '@/website/useStorageStore'

export type UserState = {
  // 鑑權：來自 login
  token?: string
  // 用戶資訊
  userInfo?: OK_API_AGENT_INDEX_LOGIN.RES['t']
} & {
  // 登出處理
  logout: () => UserState
}

/** 初始值 */
const initialState = (): UserState => ({
  token: undefined, // 可以直接拿來判斷 isLogin
  userInfo: undefined, // 用戶資訊
  logout: () => initialState(), // 登出 Callback
})

/** 用戶資訊 Store */
export const [useUserStore, setUserState] = defineStore<UserState>(initialState(), {
  name: 'USER',
  partialize: (state) => ({ userInfo: state.userInfo, token: state.token }) as UserState,
})

/** Runtime 第一時間就有 Token */
if (useUserStore.getState()?.token) {
  setHeaders({ [API_CONST.X_SESSION_TOKEN]: String(useUserStore.getState()?.token || '') })
}

/** 用 token 登入 */
export const loginByToken = (token: string) => {
  if (!token) throw 'token is empty'
  setHeaders({ [API_CONST.X_SESSION_TOKEN]: token })
  setUserState({ token })
}

/**
 * 登出處理
 * 登出所有屬性都會被移除，要保留的請存在 useStorageStore
 */
export const logout = () => {
  setHeaders({ [API_CONST.X_SESSION_TOKEN]: '' })
  // 刪除持久化數據緩存
  PersistNames.forEach((name) => {
    if (name.endsWith('STORAGE')) return
    deleteStorageItem(name)
    deleteSessionItem(name)
  })
  // 需要登出的 store 處理
  logoutCallMap.forEach((store) => {
    const { update, logout } = store.getState() ?? {}
    if (update && logout) update?.(logout?.())
  })
  /** useQuery 清理數據 */
  QueryClientInstance.clear()
}

/** 簡單判斷登入狀態 */
export const useIsLogin = () => {
  const isLogin = useUserStore((state) => !!state.token)
  return { isLogin }
}

/** 重新獲取用戶資訊 */
export const refreshUserInfo = async () => {
  await QueryClientInstance.refetchQueries({ queryKey: [`/user/info/${useUserStore.getState().token}`] })
}

/** 用戶資訊 */
export const useUserInfo = () => {
  const userInfo = useUserStore((state) => state?.userInfo)
  return useMemo(
    () => ({
      userInfo,
      isLogin: !!userInfo,
      refreshUserInfo,
    }),
    [userInfo],
  )
}
